/** Fog of war, drawn as one filtered sprite over the world.
 *
 * The temptation is one black square per unseen cell. That produces a hard
 * checkerboard edge that announces the grid, and 20736 sprites on a 144x144
 * map. Instead the fog state is written into a single texture and stretched
 * over the world in one draw.
 *
 * ## Two binary fields, not one ramp
 *
 * The first version carried ONE field - "how dark is this cell", 0 visible,
 * 0.46 remembered, 1 unseen - sampled bilinearly and then quantised to the
 * nearest of those three levels. That is wrong in a way that is invisible in
 * the code and glaring on screen: interpolating from a *visible* cell (0) to
 * an *unseen* one (1) passes straight through the remembered band, so every
 * boundary between lit ground and black grew a ~1 tile ring of "remembered"
 * fog that no cell had asked for. On day one, when nothing is remembered at
 * all, the whole explored region was ringed by it, and because that ring was
 * also multiplied by a cool grey it read as a *coastline*: the map looked like
 * an island floating in a void rather than a revealed part of a continent.
 *
 * So the fog is carried as two independent BINARY fields:
 *
 *   black   1 where the cell has never been seen
 *   dim     1 where the cell is not visible right now (unseen or remembered)
 *
 * Each is sampled bilinearly, jittered, and resolved against one half. Away
 * from a boundary a subtexel is black, or dimmed, or clear - never a blend of
 * two of them - and where nothing is remembered the two fields are identical,
 * so no band can appear. The bilinear interpolation still happens, on the CPU,
 * over the animated cell fields, but it is used only to decide where a subtexel
 * sits relative to a boundary.
 *
 * ## The edge is torn, not stepped
 *
 * A binary cut on a grid draws a staircase, and at four subtexels per cell that
 * staircase had 16px treads with square corners - the single loudest artefact
 * on the adventure map. Four things fix it:
 *
 *   SUB      eight, so a tread is 8 world px rather than 16;
 *   WARP     the sample position is displaced by a smooth two-octave value
 *            noise of up to 1.47 cells on a 3-and-7-cell lattice, which bends
 *            the whole contour into a wandering curve instead of a flight of
 *            stairs;
 *   JITTER   a fifth of a cell of threshold noise, enough to keep the remaining
 *            8px steps from lining up, far too little to detach a subtexel from
 *            its neighbours;
 *   FRINGE   the last subtexel of approach is resolved as COVERAGE rather than
 *            as a vote - the area of the subtexel the dark region takes - which
 *            is what removes the square corners the other three leave behind.
 *
 * The fourth is not the alpha ramp this file rejected twice, and the difference
 * matters. That ramp interpolated the THREE-LEVEL field and so invented a band
 * of remembered fog nobody had asked for; this leaves the two fields binary and
 * antialiases each one's own contour over one subtexel, which is 8 world pixels
 * - the width the classic spends stippling the same edge, measured at `FRINGE`.
 *
 * All four are pure functions of the subtexel's own coordinates, so the edge is
 * stable in WORLD space: it does not crawl, shimmer or re-roll when the camera
 * pans, which is what killed the ramp this replaced.
 *
 * ## Unseen is navy and has stars; REMEMBERED is still pure value
 *
 * These are two different questions and an earlier round answered them with one
 * colour. Multiplying the REMEMBERED band by a desaturated blue-grey turned
 * remembered grass navy, which is a different *place* rather than a dimmer one;
 * memory is a change of value, not of hue, and the remembered veil is still
 * pure black at `REMEMBERED_VEIL`.
 *
 * The NEVER-SEEN region is not a dimming at all - it is a substitute for the
 * map. The classic paints it as a night sky, and the sky is mostly not the stars:
 * the figures this file is built on are a clean 160x120 patch of shroud with a
 * base of RGB (1.3, 1.1, 10.9), 0.72% of pixels above luminance 40, and A SIXTH
 * OF THE REST lifted a few counts off the base by a dust field far too faint to
 * be called stars. See `dustFrames` for the histogram. Ours was flat black with
 * a dozen points on it, which is a night SKY, not unknown ground - and it was
 * read exactly that way by a critic seeing it cold.
 *
 * ## UNVERIFIABLE: every the classic number in this file
 *
 * All of them were cited to `refs/adventure/*.jpg|png`. **No such directory has
 * ever existed on this machine**, and the only reference set that does exist -
 * the critic's, at a reference screenshot - contains FIVE genuine adventure-map
 * frames (a reference screenshot, `classic8`, `classic12`, `classic19`, `classic20`, from the classic gallery
 * for *the classic: Complete*) **in every one of which the whole viewport is already
 * explored.** Checked 2026-08-30: there is not one pixel of shroud in any of
 * them; the only unexplored black in the set is inside `classic7`'s MINIMAP, which
 * is a different renderer at a different scale. The scratch shots this file also
 * quotes (`night/before/...`, `night/after/...`) are gone too, though those at
 * least are ours and `qa/shoot.mjs` can make them again.
 *
 * So: the numbers below stay, because they are the numbers this shroud was
 * tuned to and re-guessing them would be worse than keeping them. But NOTHING
 * ON DISK CAN CONFIRM ANY OF THEM. Read every "measured on" in this file as
 * "measured once, off something nobody can open now". What would settle it is
 * one native-resolution the classic adventure frame with shroud in the viewport; if
 * you find one, it belongs in the reference set with its provenance, and then
 * these numbers can be checked instead of trusted.
 *
 * So the veil's RGB carries `SHROUD_RGB` at the subtexel's own black coverage
 * (premultiplied alpha makes the common case free: the run fill still writes
 * one word), and the light in the shroud is two sprite layers over the top -
 * `starFrames` for the bright points, `dustFrames` for the faint grain between
 * them, which is five sixths of what the classic puts there and was missing here
 * entirely. See those two for why neither can live in this texture.
 *
 * The field is animated rather than snapped, so `FogRevealed` plays as a reveal
 * instead of a pop: the cell fields ease, and the hard boundary walks outward
 * across them. The texture carries a one-cell border so the map's edge is
 * fogged.
 */

import { BufferImageSource, Container, Rectangle, Sprite, Texture } from "pixi.js";
import { TILE_SIZE } from "../coords.js";
import type { CellRect } from "../coords.js";

export const FOG_UNSEEN = 0;
export const FOG_REMEMBERED = 1;
export const FOG_VISIBLE = 2;

/**
 * How far a remembered cell is darkened. Value only - the hue does not move.
 *
 * 0.14, and the number was said to come off the reference shots rather than out
 * of taste. The claim behind it is that the classic does not dim remembered ground AT
 * ALL - that the shroud is binary, ground either painted at full strength or
 * pure black, with no third value anywhere in the frame.
 *
 * **UNVERIFIABLE.** The two files it was read off,
 * A reference screenshot and
 * A reference screenshot, do not exist and
 * no reference on disk shows shroud at all (see the header). The claim is
 * plausible - The classic has no "remembered" state, only seen and unseen - but that
 * is an argument from the rules, not a measurement, and it is offered here as
 * one.
 *
 * This was 0.38, which on a scenario that opens on a scouted home region is not
 * a rim but most of the map: measured on the pre-change screenshot, grass under
 * the veil sat at RGB (46, 84, 21) against (78, 135, 30) for the same grass in
 * sight - 0.61 of the brightness over about half the map area. Next to a real
 * The classic still that reads as two different times of day on one map, and it is
 * the single reason our adventure screen looked murky beside theirs.
 *
 * It is not taken to zero because, unlike the classic, this build shows remembered
 * OBJECTS that may since have moved, and the player is owed a hint that he is
 * looking at a memory. 0.14 is about a sixth of a stop: findable if you go
 * looking for the boundary, invisible as a region.
 */
export const REMEMBERED_VEIL = 0.14;

/**
 * The colour of never-seen ground, and the ONLY place hue enters this file.
 *
 * Said to be straight off the reference: the base of a clean shroud patch in
 * A reference screenshot measuring RGB
 * (1.73, 1.53, 11.23). **That file does not exist and this triple cannot be
 * checked against anything on disk** (see the header). (2, 2, 12) - dark enough
 * to read as
 * black at a glance, blue enough that the region reads as territory rather
 * than as a hole, and with the blue carrying a point of the star light the
 * base itself cannot.
 *
 * It is the BASE and not the shroud: the dust and the stars are drawn over it,
 * and what reaches the screen is their sum. The table below compared a 128x128
 * patch of deep shroud in `night/after/adventure.1080p.png` against the same
 * size patch of the original reference, by share of pixels in each luminance band.
 * Neither file is on disk; the "ours" rows can be regenerated with
 * `qa/shoot.mjs`, the "The classic" row cannot be regenerated by anyone:
 *
 *              [0,6)  [6,10) [10,16) [16,24) [24,40)  40+   32px block sd
 *   The classic      83.85   8.34   4.16    2.02    0.92    0.72      7.91
 *   ours       84.62   7.53   4.24    1.89    0.95    0.78      4.95
 *   ours before 99.12   0.00   0.00    0.10    0.15    0.63      4.43
 *
 * It applies at alpha 1 only. The remembered band keeps RGB zero, because a
 * tint there is the "remembered grass turns navy" bug this file already fixed
 * once.
 */
export const SHROUD_RGB = Object.freeze({ r: 2, g: 2, b: 12 });

/**
 * Subtexels per cell along each axis.
 *
 * It was four, and four is too coarse: a quarter of a 64px tile is a SIXTEEN
 * PIXEL square, so every boundary that ran diagonally came out as a flight of
 * 16x16 stairs with square corners. Measured on the pre-change screenshot, the
 * shroud's east edge was seven runs of exactly 16 rows each stepping exactly 16
 * columns - a staircase, not a coastline.
 *
 * Eight puts the smallest feature at 8 world pixels - an eighth of a tile - and
 * `upload` no longer pays for it everywhere: cells whose whole 3x3
 * neighbourhood is settled on one side of the cut are filled a block at a time
 * (`blockConstant`), so the per-subtexel work is confined to the boundary
 * itself. Measured against the pre-change code on the same machine,
 * interleaved: 24.1ms a call before and 31.3ms after on a 144x144 map, for four
 * times the resolution.
 *
 * The field stays `(w+2)*SUB` square: 1168px for the largest supported map.
 */
export const SUB = 8;

/**
 * How far a subtexel's threshold may be pushed, in field units.
 *
 * The cell field ramps by 1.0 across one cell, so this is also the WIDTH of the
 * band, in cells, that the boundary is dithered over: a fifth of a tile, which
 * at `SUB` eight is a fringe about one subtexel deep.
 *
 * It was tried at 0.55 and that is too much. A band four subtexels wide does
 * not read as a soft edge; it detaches, and the shroud grows islands - lone 8px
 * squares of black sitting out in open grass and lone lit squares inside the
 * black. That judgement was held against `refs/adventure/h3hd-adventure-
 * subterranean-sidebar-1920x1080.jpg`, said to show the boundary between lit
 * rock and shroud as a single clean carved curve with no stipple and no
 * detached pixels. **That file does not exist**, so the comparison is not
 * reproducible; what remains reproducible, and is the actual reason 0.55 was
 * rejected, is the detached-island defect in our own frame. The organic shape has to come from the WARP, which moves the whole
 * contour; the jitter's job is only to stop the remaining 8px steps landing in
 * a regular flight.
 *
 * It may not reach 0.70: at 0.70 a cell sitting at 0.15 could be pushed over
 * the cut, which would punch a hole in open ground or a window into the
 * unexplored map. `test/adventure-world.spec.ts` pins both ends.
 */
const JITTER = 0.20;

/**
 * The width, in field units, over which a subtexel's alpha is its COVERAGE by
 * the black region rather than a yes/no answer about its centre.
 *
 * The cell field ramps by 1.0 across one cell and a subtexel is `1/SUB` of a
 * cell, so `(sample - CUT)/FRINGE + 0.5` at `FRINGE = 1/SUB` is exactly the
 * fraction of a subtexel the dark region takes. It is the antialiasing a
 * rasteriser does, computed from the field instead of by supersampling: 4x4
 * supersampling of the same boundary costs sixteen warps and sixteen bilinear
 * samples a subtexel, which on the measured 25.6ms boundary pass is not
 * affordable, and this is one divide.
 *
 * ## Why the band is two subtexels and not one
 *
 * One subtexel is the exact coverage, and it is too little to see. Consecutive
 * subtexels along the normal differ by a whole subtexel of field, so a band one
 * subtexel wide is crossed by at most ONE partial value per row and often by
 * none - which on the screenshot came out as the same 8px staircase with an
 * occasional grey block let into it. Two subtexels puts two steps in every
 * crossing, which is a ramp rather than a chip.
 *
 * Two is also where the reference is. The classic's stipple is 8.27px on a 32px tile
 * - 0.26 of a tile - and two subtexels here is 16px on a 64px tile, 0.25 of
 * one. It is the same fraction of the ground, which is the scale the same
 * argument sets the star and dust sizes by.
 *
 * ## Why the edge needed it, measured rather than argued
 *
 * The contour is already wavy - `WARP_SUB` bought that and the waviness test
 * below still pins it - but at `SUB` eight the boundary is drawn in EIGHT
 * WORLD PIXEL BLOCKS with square corners, and a screenshot shows that as a
 * flight of 8x8 stairs. The table below was measured on
 * `scratchpad/night/before/adventure.1080p.png` against the real thing, as the
 * number of pixels a row spends between its last full-strength ground pixel and
 * the first pixel of solid shroud. **Neither reference row can be reproduced:
 * both named files are gone and no shroud appears in any reference on disk.**
 * The "ours" rows are ours and can be:
 *
 *   The classic the original  (a reference screenshot - MISSING, 224 rows)
 *                                       8.27px mean, median 8,  8.0% hard
 *   The expansion       (`expansion-adventure-islands-sidebar-800x600.png` - MISSING, 74 rows)
 *                                       6.50px mean, median 6, 29.7% hard
 *   ours before (969 rows)              1.71px mean, median 1, 83.7% hard
 *
 * A hard cut scores 1 by construction, and ours scored 1 on five rows in six.
 * After: 6.40px mean at 1080p and 6.60px at the laptop viewport, with the hard
 * rows down from 79.6% to 26.2%. (The same statistic on the reference is 8.07;
 * The expansion's, on the same 32px tile, is 5.57.)
 * The classic spends eight pixels there, and it spends them as a one-pixel STIPPLE -
 * blown up, its edge is speckled, not blurred. We cannot stipple at one pixel:
 * the smallest mark this field can make is `TILE_SIZE/SUB` = 8px, and widening
 * the jitter to stipple at that size is what was tried at `JITTER` 0.55 and
 * rejected, because an 8px speck detached from the coast is a hole in the map
 * rather than a dither. Coverage buys the same eight pixels of transition at
 * the same cost and cannot detach anything, because a subtexel's alpha now
 * moves continuously with its distance from the contour instead of flipping.
 *
 * The jitter still rides on top, so the fringe is not a clean bevel: adjacent
 * boundary subtexels get thresholds a fifth of a cell apart and the band comes
 * out ragged, which is the point. Everything remains a pure function of the
 * subtexel's own world position, so none of it crawls when the camera pans.
 */
const FRINGE_SUBTEXELS = 2;
export const FRINGE = FRINGE_SUBTEXELS / SUB;

/**
 * How far the sampling point wanders, in subtexels, and the lattice pitch of
 * the noise that moves it.
 *
 * This is what makes the edge organic, and it does it the way the reference art
 * does: by moving the whole contour rather than by roughening it. Displacing
 * the sample position with a smooth two-octave value noise means the shroud
 * arrives at a cell early on one stretch and late on the next, so a boundary
 * that the cell grid wants to draw as a 45-degree flight of stairs comes out as
 * a wandering curve - and every subtexel along it still agrees with its
 * neighbours, so the curve stays connected.
 *
 * ## The amplitude, and why it is not 3.4
 *
 * It was 3.4 subtexels - 0.425 of a cell - because `blockConstant` tested a
 * 3x3 window, and a subtexel of cell `t` samples in `[t-0.5-A, t+0.5+A]`: the
 * moment `A` reaches 0.5 that runs past the window and the fast path could
 * fill a block flat that should have been torn.
 *
 * 0.425 of a cell is not enough to bend a boundary. The comparison was against
 * what was called a real the classic fog edge
 * (a reference screenshot, 389 rows of
 * boundary) - **a file that does not exist, so every "theirs" figure in the next
 * sentence is unverifiable.** The "ours" figures are ours and stand: our shroud
 * edge was a straight line with a wobble:
 * 5.5 runs per 100 rows against their 93.6, ZERO direction reversals over 292
 * rows of the day scene against their 61 per 100, and a waviness - the standard
 * deviation of the edge about a fitted straight line - of 0.06 to 0.11 tiles
 * against their 0.51. Theirs wanders half a tile about its local mean; ours
 * wandered a tenth. 33 of our 38 distinct edge rows sat at the same residue mod
 * 8, which is to say the boundary was still on the subtexel lattice.
 *
 * The window is now 5x5, which lifts the ceiling to 1.5 cells, and the
 * amplitude is 11.8 subtexels = 1.475 cells, strictly under it. `blockConstant`
 * runs ONCE PER CELL - 25 field reads instead of 9 - and only cells it rejects
 * pay the per-subtexel warp, so the extra cost is confined to a fixed 25/9
 * ratio on a per-cell pass that was never the expensive half. Measured on this
 * tree, interleaved A/B over 40 `computeVeil` calls on a 144x144 field: see the
 * report; the boundary path is unchanged.
 *
 * ## The lattice pitch, and why it is not 9 and 20
 *
 * The second half of the same defect. At pitch 9 and 20 subtexels - 1.1 and 2.5
 * cells - the displacement field varies FASTER THAN THE BOUNDARY IS LONG, so
 * successive stretches of contour get uncorrelated pushes that cancel: the
 * noise roughened the edge by a fraction of a subtexel instead of bending it by
 * cells. 24 and 56 subtexels (3 and 7 cells) put one lobe of noise across
 * several tiles of boundary, which is the scale the classic's fog wanders at.
 */
const WARP_SUB = 11.8;
const WARP_LATTICE = 24;
const WARP_LATTICE_2 = 56;
const WARP_SALT_X = 0x9e37;
const WARP_SALT_Y = 0x51ed;

/** The cut. A jittered sample at or above this is on the dark side. */
const CUT = 0.5;

/** Milliseconds for a newly lit cell to finish opening. */
const REVEAL_MS = 320;

export interface FogPlane {
  readonly width: number;
  readonly height: number;
  /** 0 unseen, 1 remembered, 2 visible, one byte per cell, as `ObservedMap`. */
  readonly visibility: Uint8Array;
}

interface Field {
  readonly buffer: Uint8Array;
  readonly source: BufferImageSource;
  readonly texture: Texture;
  readonly sprite: Sprite;
}

/**
 * A stable hash in [0,1) for one subtexel. Stable is the whole requirement:
 * a jitter that changed between frames would make the fog border crawl, which
 * is far more distracting than the soft edge it replaced.
 */
export function jitterAt(sx: number, sy: number): number {
  let h = Math.imul(sx | 0, 0x27d4eb2d) ^ Math.imul(sy | 0, 0x165667b1);
  h = Math.imul(h ^ (h >>> 15), 0x2545f491);
  h ^= h >>> 13;
  return ((h >>> 8) & 0xffff) / 0x10000;
}

/**
 * Which side of the boundary the CENTRE of one subtexel falls on.
 *
 * Still binary, and still the thing that decides whether a cell is settled -
 * `coverAt` below is this predicate with the last subtexel of approach
 * resolved as area rather than as a vote, and the two agree everywhere except
 * inside `FRINGE/2` of the cut.
 */
export function cutAt(sample: number, jitter: number): boolean {
  return sample + (jitter - 0.5) * JITTER >= CUT;
}

/**
 * How much of one subtexel the dark side covers, 0..1.
 *
 * `cutAt` asks where the subtexel's centre is; this asks how much of its AREA
 * the region takes, which for a field with a unit gradient is the signed
 * distance to the contour divided by the subtexel's own width. Outside the
 * fringe it is exactly 0 or exactly 1, so nothing away from a boundary changes
 * and the block fast path still fills flat.
 */
export function coverAt(sample: number, jitter: number): number {
  const d = (sample + (jitter - 0.5) * JITTER - CUT) / FRINGE + 0.5;
  return d <= 0 ? 0 : d >= 1 ? 1 : d;
}

/**
 * The widest the cut can move for one subtexel, in field units. A cell whose
 * whole neighbourhood sits further than this from `CUT` cannot be torn OR
 * partly covered, which is what makes the block fast path in `upload` exact
 * rather than a tolerance.
 *
 * Both terms are here for the same reason: the jitter can push the threshold by
 * `JITTER/2`, and the fringe spreads the answer over `FRINGE/2` either side of
 * wherever the threshold ended up. Leaving the fringe out would let a cell
 * whose sample sits `JITTER/2 + eps` from the cut be filled as one flat block
 * when a subtexel of it was owed a partial alpha - a square of hard edge inside
 * a soft one, which is the exact class of defect `blockConstant`'s byte-for-byte
 * agreement test exists to catch.
 *
 * What widening it from 0.100 to 0.225 costs, counted rather than guessed: a
 * cell falls off the fast path when `|value - CUT| < CUT_MARGIN`, and AT REST
 * every cell is exactly 0 or 1, so on a settled 146x146 field the count is ZERO
 * either way and the resting cost has not moved. Only a reveal pays: on the same
 * field eased halfway open, 336 cells were unsettled at 0.100 and 732 are at
 * 0.225, out of 21316. `computeVeil` measures 28.96ms a call at rest and 47.80ms
 * mid-reveal on that field, and the reveal is 320ms long.
 */
export const CUT_MARGIN = (JITTER + FRINGE) / 2;

/**
 * A settled subtexel value, or `null` if the jitter could still go either way.
 * Exported because it is the whole justification for filling a cell's `SUB*SUB`
 * subtexels as one block everywhere except along the boundary.
 */
export function settledCut(sample: number): boolean | null {
  if (sample + CUT_MARGIN < CUT) return false;
  if (sample - CUT_MARGIN >= CUT) return true;
  return null;
}

const smoothstep = (t: number): number => t * t * (3 - 2 * t);

/**
 * Smooth value noise in [-1, 1] on a lattice of pitch `pitch` subtexels.
 *
 * Built from `jitterAt` rather than from a second hash so there is one source
 * of stable randomness in this file; `salt` moves the lattice so the x and y
 * displacements are independent and the warp does not collapse onto the
 * diagonal.
 */
export function warpNoise(sx: number, sy: number, pitch: number, salt: number): number {
  const gx = sx / pitch;
  const gy = sy / pitch;
  const x0 = Math.floor(gx);
  const y0 = Math.floor(gy);
  const grid = inRange(x0) && inRange(y0) ? lattice(pitch, salt) : null;
  const a = grid === null ? jitterAt(x0 + salt, y0 - salt) : grid[y0 * LATTICE_SPAN + x0]!;
  const b = grid === null ? jitterAt(x0 + 1 + salt, y0 - salt) : grid[y0 * LATTICE_SPAN + x0 + 1]!;
  const c = grid === null ? jitterAt(x0 + salt, y0 + 1 - salt) : grid[(y0 + 1) * LATTICE_SPAN + x0]!;
  const d = grid === null
    ? jitterAt(x0 + 1 + salt, y0 + 1 - salt)
    : grid[(y0 + 1) * LATTICE_SPAN + x0 + 1]!;
  const tx = smoothstep(gx - x0);
  const ty = smoothstep(gy - y0);
  const top = a + (b - a) * tx;
  const bottom = c + (d - c) * tx;
  return (top + (bottom - top) * ty) * 2 - 1;
}

/**
 * The lattice of `jitterAt` values for one (pitch, salt), memoised.
 *
 * A pure cache, not a different function - every value it returns is the one
 * `jitterAt` would have returned - but the un-cached version cost the boundary
 * sixteen hashes per subtexel (four corners, two octaves, two axes) to
 * re-derive the same few thousand numbers on every reveal frame.
 *
 * Held in four named slots rather than a `Map`, because a `Map.get` per corner
 * is itself most of what the cache was meant to save: sixteen lookups per
 * subtexel measured slower than the hashing they replaced.
 */
const LATTICE_SPAN = 168;
const inRange = (g: number): boolean => g >= 0 && g < LATTICE_SPAN - 1;

let latticeA: Float32Array | null = null;
let latticeB: Float32Array | null = null;
let latticeC: Float32Array | null = null;
let latticeD: Float32Array | null = null;

function lattice(pitch: number, salt: number): Float32Array | null {
  if (pitch === WARP_LATTICE) {
    if (salt === WARP_SALT_X) return (latticeA ??= buildLattice(salt));
    if (salt === WARP_SALT_Y) return (latticeB ??= buildLattice(salt));
    return null;
  }
  if (pitch === WARP_LATTICE_2) {
    if (salt === (WARP_SALT_X ^ 0x2f1b)) return (latticeC ??= buildLattice(salt));
    if (salt === (WARP_SALT_Y ^ 0x2f1b)) return (latticeD ??= buildLattice(salt));
  }
  return null;
}

function buildLattice(salt: number): Float32Array {
  const grid = new Float32Array(LATTICE_SPAN * LATTICE_SPAN);
  for (let gy = 0; gy < LATTICE_SPAN; gy++) {
    for (let gx = 0; gx < LATTICE_SPAN; gx++) {
      grid[gy * LATTICE_SPAN + gx] = jitterAt(gx + salt, gy - salt);
    }
  }
  return grid;
}

/** Veil opacity for a subtexel, from the two cut fields. */
export function veilAlpha(black: boolean, dim: boolean): number {
  return black ? 1 : dim ? REMEMBERED_VEIL : 0;
}

/**
 * The same opacity from the two COVERAGES, which is what a partly covered
 * subtexel at the boundary has instead of a pair of booleans.
 *
 * `kd >= kb` always - a never-seen subtexel is also a not-visible one - so the
 * subtexel is `kb` black at opacity 1 and a further `kd - kb` remembered at
 * `REMEMBERED_VEIL`. At the corners this is `veilAlpha` exactly, which is why
 * the three resting levels of the veil have not moved.
 */
export function veilAlphaCovered(kb: number, kd: number): number {
  return kb + (kd - kb) * REMEMBERED_VEIL;
}

/**
 * Bilinear sample of a cell field at subtexel coordinate `(sx, sy)`, which may
 * be fractional because the warp moves it.
 *
 * Subtexel `(sx, sy)` sits at cell coordinate `(sx + 0.5)/sub - 0.5` in the
 * field's own indexing, which puts the centre subtexel of a cell exactly on
 * that cell's value and the corner ones halfway to their neighbours.
 */
export function sampleField(
  field: Float32Array, tw: number, th: number, sx: number, sy: number, sub = SUB,
): number {
  const fx = (sx + 0.5) / sub - 0.5;
  const fy = (sy + 0.5) / sub - 0.5;
  const x0 = Math.max(0, Math.min(tw - 1, Math.floor(fx)));
  const y0 = Math.max(0, Math.min(th - 1, Math.floor(fy)));
  const x1 = Math.min(tw - 1, x0 + 1);
  const y1 = Math.min(th - 1, y0 + 1);
  const tx = Math.max(0, Math.min(1, fx - x0));
  const ty = Math.max(0, Math.min(1, fy - y0));
  const a = field[y0 * tw + x0]!;
  const b = field[y0 * tw + x1]!;
  const c = field[y1 * tw + x0]!;
  const d = field[y1 * tw + x1]!;
  const top = a + (b - a) * tx;
  const bottom = c + (d - c) * tx;
  return top + (bottom - top) * ty;
}

/** Two octaves of value noise, in subtexels, for one axis of the warp. */
export function warpOffset(sx: number, sy: number, salt: number): number {
  return WARP_SUB * (
    warpNoise(sx, sy, WARP_LATTICE, salt) * 0.68
    + warpNoise(sx, sy, WARP_LATTICE_2, salt ^ 0x2f1b) * 0.32
  );
}

/**
 * One subtexel of the veil, as a premultiplied RGBA word.
 *
 * A word rather than an alpha byte because the shroud's navy is no longer a
 * property of "alpha is 255": a subtexel the boundary crosses carries the tint
 * at its own coverage, and premultiplied alpha means that IS the stored RGB.
 * Returning the packed word keeps the byte path and the word path deriving the
 * same four numbers from one place.
 *
 * `sub` is the grid this subtexel belongs to, which is not always `SUB` - see
 * `FINE_SUB`. The warp is a function of WORLD position and must stay one, or
 * the contour would move when the grid was refined: the noise is evaluated at
 * `sx * SUB / sub`, which is where this subtexel sits in `SUB` units, and its
 * answer - which `warpOffset` returns in `SUB` units, because that is what the
 * test pins it in - is converted back into this grid's units before it is added
 * to `sx`.
 */
function subtexelVeil(
  black: Float32Array, dim: Float32Array, tw: number, th: number,
  sx: number, sy: number, sub: number,
): number {
  const toCoarse = SUB / sub;
  const cx = sx * toCoarse;
  const cy = sy * toCoarse;
  const wx = sx + warpOffset(cx, cy, WARP_SALT_X) / toCoarse;
  const wy = sy + warpOffset(cx, cy, WARP_SALT_Y) / toCoarse;
  const j = jitterAt(sx, sy);
  const kb = coverAt(sampleField(black, tw, th, wx, wy, sub), j);
  const kd = Math.max(kb, coverAt(sampleField(dim, tw, th, wx, wy, sub), j));
  return coveredWord(kb, veilAlphaCovered(kb, kd));
}

/**
 * The one alpha byte every subtexel of cell `(tx, ty)` takes, or -1 when the
 * cell straddles a boundary and has to be resolved subtexel by subtexel.
 *
 * The window is exactly the reach of a subtexel of cell `t`: the bilinear
 * sample of a subtexel inside `t` lands in `[t-0.5, t+0.5)` before the warp and
 * `[t-0.5-A, t+0.5+A)` after it, with `A = WARP_SUB/SUB` cells. The bilinear
 * tap of a sample at cell coordinate `f` reads cells `floor(f)` and
 * `floor(f)+1`, so a window of radius `R` is exact while `A <= R - 0.5`.
 *
 * `BLOCK_R` is therefore not a taste knob and not a tolerance: it is the
 * smallest radius that covers `WARP_SUB`, and `WARP_CAP_CELLS` below is the
 * inequality written down so a future edit to either constant trips the test in
 * `test/adventure-shroud.spec.ts` instead of silently punching square holes in
 * the shroud. At radius 1 the amplitude ceiling is half a cell, which is why
 * the warp used to be too small to bend anything; at radius 2 it is 1.5 cells.
 *
 * If all `(2R+1)^2` are settled to the same side of the cut then so is every
 * convex combination of any four of them, whatever the jitter does, so this is
 * an exact shortcut.
 */
const BLOCK_R = 2;

/** The largest `WARP_SUB / SUB` the block fast path stays exact for. */
export const WARP_CAP_CELLS = BLOCK_R - 0.5;

/** The warp amplitude actually in use, in cells. Must stay under the cap. */
export const WARP_AMPLITUDE_CELLS = WARP_SUB / SUB;

/**
 * A cell's settled state as one byte: bit 0 black, bit 1 dim, or `UNSETTLED`
 * when the jitter could still push either field over the cut.
 */
const UNSETTLED = 255;

/**
 * ## Why the window is separable, and what it cost before it was
 *
 * The naive form of this test - read all `(2R+1)^2` neighbours and call
 * `settledCut` on each - is what a 3x3 window can afford and a 5x5 window
 * cannot. It was predicted to be negligible "because the window test runs once
 * per cell"; measured, it was not. Four builds of this file, interleaved, 20
 * calls each on a 146x146 field with an explored disc in the middle:
 *
 *   3x3 window, warp 3.4   12.62 ms      3x3 window, warp 11.8   12.50 ms
 *   5x5 window, warp 3.4   24.70 ms      5x5 window, warp 11.8   24.22 ms
 *
 * The warp amplitude costs NOTHING - widening it does not move the number - and
 * the window costs everything: 5x5 doubled the call. The reason is that the
 * naive form evaluates every cell's `settledCut` 25 times, once for each
 * neighbour whose window contains it, and `settledCut` is two float compares
 * on a `Float32Array` read.
 *
 * "All 25 equal" is separable, so it does not have to. Reduce each cell to one
 * byte once, take a horizontal 5-window of "all equal" over that, then a
 * vertical 5-window over the result: 1 + 5 + 5 byte reads a cell instead of 25
 * reads and 50 float compares, and the answer is identical - which the
 * byte-for-byte agreement test in `test/adventure-shroud.spec.ts` checks
 * against the per-subtexel path on every run.
 *
 * That recovers about a sixth. THE REST IS REAL AND IS NOT AN OPTIMISATION
 * PROBLEM: a radius-2 window rejects a boundary band two cells wide instead of
 * one, and every rejected cell resolves 64 subtexels through the warp. Final
 * interleaved A/B, 30 calls each on the same field:
 *
 *   3x3 naive window, warp 3.4 (before)     13.72 ms
 *   5x5 separable window, warp 11.8 (after) 25.60 ms
 *
 * `upload` runs once per `advance` while a reveal eases, so on the largest map
 * that is a 320ms animation costing about twice what it did. It is the price of
 * the wavy edge and it is worth reporting rather than hiding: the next lever,
 * if it ever matters, is that `warpNoise`'s four lattice corners are CONSTANT
 * across a run of `pitch` subtexels in a row, so the boundary loop can hoist
 * them instead of re-reading four values per octave per axis per subtexel.
 */
let stateScratch = new Uint8Array(0);
let rowScratch = new Uint8Array(0);

function cellStates(
  black: Float32Array, dim: Float32Array, n: number,
): Uint8Array {
  if (stateScratch.length < n) stateScratch = new Uint8Array(n);
  const out = stateScratch;
  for (let i = 0; i < n; i++) {
    const b = settledCut(black[i]!);
    if (b === null) { out[i] = UNSETTLED; continue; }
    const d = settledCut(dim[i]!);
    if (d === null) { out[i] = UNSETTLED; continue; }
    out[i] = (b ? 1 : 0) | (d ? 2 : 0);
  }
  return out;
}

/** Horizontal half of the window: the row's state, or `UNSETTLED` if it varies. */
function rowWindows(state: Uint8Array, tw: number, th: number): Uint8Array {
  const n = tw * th;
  if (rowScratch.length < n) rowScratch = new Uint8Array(n);
  const out = rowScratch;
  for (let y = 0; y < th; y++) {
    const base = y * tw;
    for (let x = 0; x < tw; x++) {
      let v = state[base + clampIndex(x - BLOCK_R, tw)]!;
      for (let dx = -BLOCK_R + 1; dx <= BLOCK_R; dx++) {
        if (state[base + clampIndex(x + dx, tw)]! !== v) { v = UNSETTLED; break; }
      }
      out[base + x] = v;
    }
  }
  return out;
}

const clampIndex = (i: number, n: number): number => (i < 0 ? 0 : i >= n ? n - 1 : i);

/**
 * The one alpha byte every subtexel of cell `(tx, ty)` takes, or -1 when the
 * cell straddles a boundary and has to be resolved subtexel by subtexel.
 *
 * The window is exactly the reach of a subtexel of cell `t`: the bilinear
 * sample of a subtexel inside `t` lands in `[t-0.5, t+0.5)` before the warp and
 * `[t-0.5-A, t+0.5+A)` after it, with `A = WARP_SUB/SUB` cells. The bilinear
 * tap of a sample at cell coordinate `f` reads cells `floor(f)` and
 * `floor(f)+1`, so a window of radius `R` is exact while `A <= R - 0.5`.
 *
 * `BLOCK_R` is therefore not a taste knob and not a tolerance: it is the
 * smallest radius that covers `WARP_SUB`, and `WARP_CAP_CELLS` is the
 * inequality written down so a future edit to either constant trips the test in
 * `test/adventure-shroud.spec.ts` instead of silently punching square holes in
 * the shroud. At radius 1 the amplitude ceiling is half a cell, which is why
 * the warp used to be too small to bend anything; at radius 2 it is 1.5 cells.
 *
 * If all `(2R+1)^2` are settled to the same side of the cut then so is every
 * convex combination of any four of them, whatever the jitter does, so this is
 * an exact shortcut.
 */
function blockConstant(rows: Uint8Array, tw: number, th: number, tx: number, ty: number): number {
  const v = rows[clampIndex(ty - BLOCK_R, th) * tw + tx]!;
  if (v === UNSETTLED) return -1;
  for (let dy = -BLOCK_R + 1; dy <= BLOCK_R; dy++) {
    if (rows[clampIndex(ty + dy, th) * tw + tx]! !== v) return -1;
  }
  const b0 = (v & 1) !== 0;
  const d0 = (v & 2) !== 0;
  return Math.round(veilAlpha(b0, b0 || d0) * 255);
}

/**
 * The whole veil, one alpha byte per subtexel, written into `out` as the alpha
 * channel of a premultiplied RGBA buffer whose RGB stays zero.
 *
 * `blocks` is a test seam and nothing else. With it on - which is what the
 * renderer does - a cell whose whole neighbourhood has settled is filled a
 * block at a time and only cells straddling a boundary pay for the warp and
 * the hash; with it off every subtexel is resolved on its own. The two must
 * agree byte for byte, and `test/adventure-shroud.spec.ts` asserts that, because
 * a fast path that silently skipped a boundary cell would draw a square hole in
 * the shroud that no unit test of the pure cut would ever see.
 */
export function computeVeil(
  black: Float32Array, dim: Float32Array, tw: number, th: number,
  out: Uint8Array, blocks = true,
): void {
  computeVeilAt(black, dim, tw, th, out, SUB, blocks);
}

/**
 * ## The same veil on a finer grid, and why `SUB` could not simply be raised
 *
 * `SUB` is the resolution of the DECISION - which cells are settled, how wide
 * the coverage fringe is in field units - and it is pinned: `FRINGE` is
 * `2/SUB` and both are asserted at their present values in
 * `test/adventure-shroud.spec.ts`, so moving `SUB` moves the width of the band
 * this file spent a round tuning. What was left was the size of the MARK: at
 * `SUB` eight the smallest thing the veil texture can draw is `TILE_SIZE/SUB` =
 * 8 world pixels, so the boundary came out as a flight of 8x8 squares with
 * square corners and the fringe was two of them - two levels, which reads as
 * one step, not as a transition. Measured on `shots0/adventure.1080p.png` at 6x
 * magnification: the shroud's east edge is 8px blocks and the whole crossing
 * from open water to solid shroud holds one intermediate value.
 *
 * So the grid the TEXTURE is written on is separated from the grid the cut is
 * decided on. Everything that decides anything - `CUT_MARGIN`, `settledCut`,
 * `cellStates`, `rowWindows`, `blockConstant`, `FRINGE` - is per CELL or in
 * field units and does not move. Only the number of subtexels a cell is
 * rasterised into changes, which turns the 8px block into a 4px one and the
 * two-level fringe into four levels over the same 16 world pixels.
 *
 * The cost is exactly quadratic and is why this is not simply always on: the
 * buffer is `((n+2)*sub)^2 * 4` bytes for an n-cell map, so 144 cells is 5.46 MB
 * at eight and 21.83 MB at sixteen, and every `upload` re-sends the whole of it
 * while a reveal eases. `FINE_VEIL_BUDGET` is that arithmetic turned into a map
 * size: the fine grid is used while the buffer stays under 8 MB, which is every
 * map up to 88 cells a side, and above that the shroud keeps the 8px mark it
 * has today. A large map trading a coarser fog edge for 16 MB of per-frame
 * upload is the right way round, and stating the threshold here is better than
 * discovering it as a stutter.
 */
export const FINE_SUB = 16;
const FINE_VEIL_BUDGET = 8 * 1024 * 1024;

/** The subtexel grid a field of `tw` x `th` cells is rasterised on. */
export const veilSubFor = (tw: number, th: number): number =>
  tw * FINE_SUB * th * FINE_SUB * 4 <= FINE_VEIL_BUDGET ? FINE_SUB : SUB;

function computeVeilAt(
  black: Float32Array, dim: Float32Array, tw: number, th: number,
  out: Uint8Array, sub: number, blocks: boolean,
): void {
  const w = tw * sub;
  // One 32-bit write per subtexel instead of index arithmetic and a byte
  // write, and - the part that matters - a whole run of settled cells goes out
  // as one `fill`. On a 144x144 map the interior is a few dozen fills rather
  // than 1.3 million scalar stores, which is the difference between 66ms and
  // 3ms a frame while the fog is opening.
  const words = wordView(out);
  const flat = new Int16Array(tw);
  const rows = blocks ? rowWindows(cellStates(black, dim, tw * th), tw, th) : null;

  for (let ty = 0; ty < th; ty++) {
    for (let tx = 0; tx < tw; tx++) {
      flat[tx] = rows === null ? -1 : blockConstant(rows, tw, th, tx, ty);
    }
    for (let sy = ty * sub; sy < (ty + 1) * sub; sy++) {
      const row = sy * w;
      let tx = 0;
      while (tx < tw) {
        const v = flat[tx]!;
        if (v < 0) {
          for (let sx = tx * sub; sx < (tx + 1) * sub; sx++) {
            const word = subtexelVeil(black, dim, tw, th, sx, sy, sub);
            if (words === null) writeWordBytes(out, row + sx, word);
            else words[row + sx] = word;
          }
          tx++;
          continue;
        }
        let end = tx + 1;
        while (end < tw && flat[end] === v) end++;
        if (words === null) {
          for (let sx = tx * sub; sx < end * sub; sx++) writeVeilBytes(out, row + sx, v);
        } else {
          words.fill(veilWord(v), row + tx * sub, row + end * sub);
        }
        tx = end;
      }
    }
  }
}

/**
 * A `Uint32Array` over the same bytes, or `null` where a word write would not
 * put the byte in the alpha lane.
 *
 * The buffer is RGBA with RGB pinned at zero, so on a little-endian machine one
 * word is `alpha << 24` and a run of subtexels is a single `fill`. Every
 * platform this renderer runs on is little-endian, but the fallback costs one
 * branch and means the fast path can never silently write the alpha into the
 * red channel on one that is not.
 */
function wordView(out: Uint8Array): Uint32Array | null {
  if (out.byteOffset % 4 !== 0 || out.length % 4 !== 0) return null;
  const probe = new Uint8Array(4);
  new Uint32Array(probe.buffer)[0] = 0xff000000;
  if (probe[3] !== 0xff) return null;
  return new Uint32Array(out.buffer, out.byteOffset, out.length / 4);
}

/**
 * One premultiplied RGBA word for a veil alpha.
 *
 * The tint rides in the RGB of the fully-opaque level only, which is what makes
 * it free: a run of never-seen subtexels is still a single `Uint32Array.fill`,
 * exactly as it was when the RGB was zero. At alpha 255 premultiplication is
 * the identity, so the stored bytes are `SHROUD_RGB` itself.
 */
const SHROUD_WORD = (
  (255 << 24) | (SHROUD_RGB.b << 16) | (SHROUD_RGB.g << 8) | SHROUD_RGB.r
) >>> 0;

export const veilWord = (a: number): number =>
  a === 255 ? SHROUD_WORD : ((a << 24) >>> 0);

/**
 * One premultiplied RGBA word for a subtexel the boundary crosses.
 *
 * The tint is the black veil's, so it is carried at the BLACK coverage and not
 * at the composite alpha: a subtexel that is a tenth never-seen over remembered
 * ground is a tenth of the navy over nine tenths of a neutral dim, and tinting
 * the whole of it would put a blue rim back around every remembered edge -
 * the bug this file's header already records once.
 */
function coveredWord(kb: number, alpha: number): number {
  const a = Math.round(alpha * 255);
  if (a === 0) return 0;
  if (kb >= 1) return a === 255 ? SHROUD_WORD : ((a << 24) | veilTint(1)) >>> 0;
  return ((a << 24) | veilTint(kb)) >>> 0;
}

/** The premultiplied navy for a black coverage, packed into the low three bytes. */
const veilTint = (kb: number): number =>
  (Math.round(SHROUD_RGB.b * kb) << 16)
  | (Math.round(SHROUD_RGB.g * kb) << 8)
  | Math.round(SHROUD_RGB.r * kb);

function writeVeilBytes(out: Uint8Array, texel: number, a: number): void {
  writeWordBytes(out, texel, veilWord(a));
}

/** The word path's bytes, for the platform where a word write would misplace them. */
function writeWordBytes(out: Uint8Array, texel: number, word: number): void {
  const i = texel * 4;
  out[i] = word & 0xff;
  out[i + 1] = (word >>> 8) & 0xff;
  out[i + 2] = (word >>> 16) & 0xff;
  out[i + 3] = (word >>> 24) & 0xff;
}

/**
 * ## The starfield, and why it is not in the veil texture
 *
 * The classic's shroud is not one colour, it is a night sky: measured on the original
 * reference, 0.76% of shroud pixels sit above luminance 40, in blobs averaging
 * 2.4 px and never bigger than 8 px, on a 32 px tile. Scaled to this build's 64
 * px tile that is about three stars a cell, each 2-3 px across.
 *
 * The veil texture cannot carry them. Its resolution is `SUB` subtexels a cell,
 * so its smallest addressable mark is 8 world pixels square - seven times the
 * area of a star, and a hard 8x8 square in a night sky reads as a dead pixel,
 * not as a star. Raising `SUB` to fix that is the expensive answer: the field
 * is `(w+2)*SUB` square in RGBA, which for a 144x144 map is 1168^2*4 = 5.46 MB
 * today and 2336^2*4 = 21.83 MB at `SUB` 16. (Worth writing down because the
 * 21.8 MB figure has been attributed to `SUB` 16 and to `SUB` 32 in different
 * places; `veilBytes` below is the arithmetic, and `test/adventure-shroud.spec.ts`
 * pins it.)
 *
 * So the stars are ordinary world-space sprites: one per cell, from a 64-tile
 * atlas chosen by a hash of the cell, drawn over the veil. A cell gets stars
 * only when its whole `BLOCK_R` window is settled black - which is exactly the
 * condition under which `blockConstant` fills that cell flat, so a star can
 * never land on a subtexel the warp left lit. The cost of that guarantee is a
 * two-cell band inside the shroud edge with no stars; the alternative is stars
 * floating over open ground, which is worse.
 */
export const STAR_TILE = 64;
const STAR_COLS = 8;
const STAR_ROWS = 8;
const STAR_SALT = 0x1b873593;
const DUST_SALT = 0x27d4eb2d;

let sharedStarFrames: Texture[] | null = null;
let sharedDustFrames: Texture[] | null = null;

/** Bytes the veil buffer occupies for a square map of `n` cells a side. */
export const veilBytes = (n: number, sub = SUB): number => ((n + 2) * sub) ** 2 * 4;

/**
 * 64 star tiles on one source, so the whole starfield is a single draw batch.
 *
 * Every value comes from `jitterAt`, the same stable hash the veil edge uses,
 * so the sky is identical on every client and across runs.
 */
function starFrames(): Texture[] {
  if (sharedStarFrames !== null) return sharedStarFrames;
  const canvas = document.createElement("canvas");
  canvas.width = STAR_TILE * STAR_COLS;
  canvas.height = STAR_TILE * STAR_ROWS;
  const ctx = canvas.getContext("2d");
  if (ctx === null) throw new Error("fog: no 2d context");
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  for (let ty = 0; ty < STAR_ROWS; ty++) {
    for (let tx = 0; tx < STAR_COLS; tx++) {
      const id = ty * STAR_COLS + tx;
      // 3 to 5 a tile. It was 2 to 4, and the count is measurable without a
      // screenshot because the marks are axis-aligned rects: replaying this
      // placement over the 64 tiles with the real `jitterAt` gives 0.452% of a
      // tile above luminance 40 at 2-to-4 and 0.600% at 3-to-5, against the
      // reference's 0.720%. The 0.452% agrees with the 0.464% measured over
      // twenty 24800-pixel boxes of deep shroud on the pre-change screenshot,
      // which is what makes the atlas figure worth trusting for the new one -
      // the regenerated scenes no longer hold shroud deep enough to carry
      // stars, so this could not be re-measured on screen.
      const n = 3 + Math.floor(jitterAt(id, STAR_SALT) * 3);
      for (let i = 0; i < n; i++) {
        const px = jitterAt(id * 31 + i, STAR_SALT + 1) * STAR_TILE;
        const py = jitterAt(id * 31 + i, STAR_SALT + 2) * STAR_TILE;
        // Most stars are faint; a few are bright. The classic's shroud runs from
        // luminance 40 up to about 160 with the mass near the bottom.
        const t = jitterAt(id * 31 + i, STAR_SALT + 3);
        const v = 44 + Math.round(t * t * 116);
        const r = Math.round(v * 0.92);
        const g = Math.round(v * 0.92);
        const b = Math.min(255, Math.round(v * 1.06));
        // A 2x2 core with a half-strength cross around it. The reference's
        // stars are 1-2 px on a 32 px tile; this build's tile is 64 px, so the
        // same star RELATIVE TO THE GROUND is twice as wide. Matching the
        // reference's pixel size instead would put three stars a cell on screen
        // at half the apparent size, which reads as dust rather than as sky.
        const X = Math.floor(tx * STAR_TILE + px);
        const Y = Math.floor(ty * STAR_TILE + py);
        ctx.fillStyle = `rgb(${r},${g},${b})`;
        ctx.fillRect(X, Y, 2, 2);
        ctx.fillStyle = `rgba(${r},${g},${b},0.45)`;
        ctx.fillRect(X - 1, Y, 1, 2);
        ctx.fillRect(X + 2, Y, 1, 2);
        ctx.fillRect(X, Y - 1, 2, 1);
        ctx.fillRect(X, Y + 2, 2, 1);
      }
    }
  }
  const source = Texture.from(canvas);
  const frames: Texture[] = [];
  for (let ty = 0; ty < STAR_ROWS; ty++) {
    for (let tx = 0; tx < STAR_COLS; tx++) {
      frames.push(new Texture({
        source: source.source,
        frame: new Rectangle(tx * STAR_TILE, ty * STAR_TILE, STAR_TILE, STAR_TILE),
        label: `fog.star.${ty * STAR_COLS + tx}`,
      }));
    }
  }
  sharedStarFrames = frames;
  return frames;
}

/**
 * ## The dust, and why flat black plus stars was not the classic's shroud
 *
 * The stars above were said to be measured off the reference. What was missed is
 * everything BETWEEN them. The histogram below is a 160x120 patch of clean
 * shroud in a reference screenshot - 19200 pixels with no
 * ground, no sidebar and no object in it. **That file does not exist and no
 * reference on disk contains any shroud at all** (see this file's header), so
 * neither the stars nor the dust can be re-derived; what follows is the shape
 * this shroud was built to, not a measurement anyone can repeat:
 *
 *   luminance   share    mean RGB
 *   0 .. 6      83.85%   (1.3, 1.1, 10.9)   the base
 *   6 .. 10      8.34%   (7.2, 6.8, 17.6)
 *   10 .. 16     4.16%   (11.8, 11.5, 22.3)
 *   16 .. 24     2.02%   (18.7, 18.6, 29.8)
 *   24 .. 40     0.92%   (28.4, 29.0, 41.9)
 *   40 and up    0.72%   the stars
 *
 * A SIXTH of the shroud is lifted off the base, almost all of it far too faint
 * to be called a star. Ours, on the same size patch of
 * `night/before/adventure.1080p.png`, had 0.46% above luminance 40 and
 * SIXTEEN PIXELS in the 10-20 band out of 19200 - the base, and then nothing,
 * and then stars. The mean colours agreed to a point and a half, which is why
 * this survived a colour check: the defect is not the colour, it is that the
 * field between the stars is empty.
 *
 * That is the whole of the "it reads as outer space" verdict. Empty black with
 * a dozen isolated points on it IS a night sky; a dense fine grain over the
 * same base is murk, and murk is what unexplored ground looks like. It is also
 * most of a blank-frame score: a 32x32 block of flat black has a luminance
 * standard deviation of about zero, and the critic's blank measure counts every
 * block under 4. Measured over the same patches, the classic's shroud gives a mean
 * block deviation of 7.91 and no blank blocks at all; ours gave 3.44 and 61%
 * blank. (The JPEG is not inventing that texture: a genuinely black region of
 * the same file - the empty minimap at (645,45)-(725,125) - measures exactly
 * 0.00 deviation, so the compressor is not adding grain to flat areas.)
 *
 * The dust is a second sprite layer rather than more marks on the star tile
 * because the two need different permission to be drawn. A star is bright
 * enough that one landing on lit grass is a defect, so stars are confined to
 * cells whose whole `BLOCK_R` window has settled black. Dust at these
 * amplitudes is not: it is drawn ADDITIVELY, so the worst it can do to ground
 * the warp pulled into a shroud cell is add five to twenty-six counts of light
 * to a 2px dot. That buys the gate a cell of its own - the cell's own state,
 * not its neighbourhood - and with it the thing that made the wider gate
 * necessary: at this density the two-cell starless band inside the shroud edge
 * stops being invisible and becomes a dark halo drawn around every explored
 * region.
 *
 * The grain is 2px on a 64px tile, which is the classic's 1px on a 32px tile at the
 * same size RELATIVE TO THE GROUND - the same argument the star size is chosen
 * by. Coverage is matched per band, so the histogram above is what the layer
 * reproduces, not something in its neighbourhood. Measured after, on a 128x128
 * patch of deep shroud: 84.62 / 7.53 / 4.24 / 1.89 / 0.95 / 0.78 against the
 * reference's 83.85 / 8.34 / 4.16 / 2.02 / 0.92 / 0.72, every band inside a
 * point. The 32x32 block deviation went 4.43 -> 4.95 on that patch and the
 * blank blocks 43.8% -> 31.2%; the reference is 7.91 and 0.0%, and the rest of
 * that gap is star DENSITY, not dust.
 */
export const DUST_PX = 2;
export const DUST_CELLS = STAR_TILE / DUST_PX;

/**
 * The last place in this file a grain could land, so a `DUST_PX` mark stays
 * inside its own tile.
 *
 * The grains used to be placed as `cell * DUST_PX`, a cell of a
 * `DUST_CELLS` x `DUST_CELLS` lattice, and that is a different picture from the
 * one the histogram above describes. Every mark then began on an EVEN pixel of
 * its tile and every tile begins on an even world pixel, so the whole shroud's
 * grain sat on one 2px lattice: not a dust field but a 50% ordered dither, and
 * it was read exactly that way by a critic seeing it cold. The count, the size
 * and the four bands are unchanged - those are what the histogram pins - and
 * only the address is now a free pixel of the tile rather than a cell of a
 * grid. `DUST_CELLS` stays as the arithmetic the band counts were derived from.
 */
const DUST_SPAN = STAR_TILE - DUST_PX + 1;

/**
 * The four dust bands: how many of the `DUST_CELLS^2` cells of a tile each one
 * takes, and what it ADDS to `SHROUD_RGB`.
 *
 * The counts are the shares above times 1024, rounded: 8.34% -> 85, 4.16% ->
 * 43, 2.02% -> 21, 0.92% -> 9. The additions are each band's mean RGB minus the
 * base this build paints, so the sum lands on the classic's measured colours.
 */
export const DUST_BANDS: readonly { readonly n: number; readonly add: readonly [number, number, number] }[]
  = Object.freeze([
    { n: 85, add: [5, 5, 6] },
    { n: 43, add: [10, 9, 10] },
    { n: 21, add: [17, 17, 18] },
    { n: 9, add: [26, 27, 30] },
  ]);

/** 64 dust tiles on one source, so the whole layer is a single draw batch. */
function dustFrames(): Texture[] {
  if (sharedDustFrames !== null) return sharedDustFrames;
  const canvas = document.createElement("canvas");
  canvas.width = STAR_TILE * STAR_COLS;
  canvas.height = STAR_TILE * STAR_ROWS;
  const ctx = canvas.getContext("2d");
  if (ctx === null) throw new Error("fog: no 2d context");
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  for (let ty = 0; ty < STAR_ROWS; ty++) {
    for (let tx = 0; tx < STAR_COLS; tx++) {
      const id = ty * STAR_COLS + tx;
      let seed = id * 4096;
      for (const band of DUST_BANDS) {
        ctx.fillStyle = `rgb(${String(band.add[0])},${String(band.add[1])},${String(band.add[2])})`;
        for (let i = 0; i < band.n; i++) {
          // Rejection-free: two hashes give a pixel, and an overlap only means
          // two grains of different bands share part of a mark, which is what a
          // real dust field does anyway.
          const gx = Math.floor(jitterAt(seed, DUST_SALT) * DUST_SPAN);
          const gy = Math.floor(jitterAt(seed, DUST_SALT + 1) * DUST_SPAN);
          seed++;
          ctx.fillRect(tx * STAR_TILE + gx, ty * STAR_TILE + gy, DUST_PX, DUST_PX);
        }
      }
    }
  }
  const source = Texture.from(canvas);
  const frames: Texture[] = [];
  for (let ty = 0; ty < STAR_ROWS; ty++) {
    for (let tx = 0; tx < STAR_COLS; tx++) {
      frames.push(new Texture({
        source: source.source,
        frame: new Rectangle(tx * STAR_TILE, ty * STAR_TILE, STAR_TILE, STAR_TILE),
        label: `fog.dust.${ty * STAR_COLS + tx}`,
      }));
    }
  }
  sharedDustFrames = frames;
  return frames;
}

/** Which of the 64 tiles a cell shows. Stable in world space, like everything else here. */
const hashCell = (x: number, y: number): number =>
  Math.floor(jitterAt(x + 0x5bd1, y - 0x2c9f) * 0x10000);

/**
 * True when every cell in the `BLOCK_R` window around `(tx, ty)` has settled to
 * never-seen. Identical in form to `blockConstant`'s test, and deliberately so:
 * this is the predicate that says "every subtexel of this cell is black,
 * whatever the warp and the jitter do".
 */
export function cellFullyBlack(
  black: Float32Array, tw: number, th: number, tx: number, ty: number,
): boolean {
  for (let dy = -BLOCK_R; dy <= BLOCK_R; dy++) {
    const y = Math.max(0, Math.min(th - 1, ty + dy));
    for (let dx = -BLOCK_R; dx <= BLOCK_R; dx++) {
      const x = Math.max(0, Math.min(tw - 1, tx + dx));
      if (settledCut(black[y * tw + x]!) !== true) return false;
    }
  }
  return true;
}

/**
 * True when the cell ITSELF has settled to never-seen, whatever its neighbours
 * are doing. The dust layer's gate, and the reason it can have one: additive
 * grains cannot damage the ground the warp may have pulled into this cell, so
 * the dust does not have to buy the two-cell exclusion zone the stars do.
 */
export function cellSettledBlack(
  black: Float32Array, tw: number, th: number, tx: number, ty: number,
): boolean {
  if (tx < 0 || ty < 0 || tx >= tw || ty >= th) return false;
  return settledCut(black[ty * tw + tx]!) === true;
}

export class FogLayer {
  readonly container = new Container({ label: "fog" });
  /** Above the veil, because the veil it sits on is opaque. */
  private readonly starsC = new Container({ label: "fog.stars" });
  private readonly starPool: Sprite[] = [];
  private starsUsed = 0;
  /** Below the stars and above the veil; additive, so it only ever adds light. */
  private readonly dustC = new Container({ label: "fog.dust" });
  private readonly dustPool: Sprite[] = [];
  private dustUsed = 0;
  private view: CellRect | null = null;

  private veil: Field | null = null;
  /** Per-CELL "never seen", eased toward its target; the animation lives here. */
  private blackTarget = new Float32Array(0);
  private blackCurrent = new Float32Array(0);
  /** Per-CELL "not visible right now". */
  private dimTarget = new Float32Array(0);
  private dimCurrent = new Float32Array(0);
  private tw = 0;
  private th = 0;
  /** Subtexels per cell this map's veil texture is written at; see `FINE_SUB`. */
  private sub = SUB;
  private animating = false;

  /** Adopt a new fog state. The field animates toward it. */
  setPlane(plane: FogPlane, z: number, instant = false): void {
    const tw = plane.width + 2;
    const th = plane.height + 2;
    const resized = this.veil === null || tw !== this.tw || th !== this.th;
    if (resized) {
      this.dispose();
      this.tw = tw;
      this.th = th;
      this.sub = veilSubFor(tw, th);
      this.veil = this.makeField("fog.veil");
      this.container.addChild(this.veil.sprite);
      // Dust first, then stars: a star is opaque and has to win where one
      // lands on a grain.
      for (const c of [this.dustC, this.starsC]) {
        if (c.parent === null) this.container.addChild(c);
        else this.container.setChildIndex(c, this.container.children.length - 1);
      }
      const n = tw * th;
      this.blackTarget = new Float32Array(n);
      this.blackCurrent = new Float32Array(n);
      this.dimTarget = new Float32Array(n);
      this.dimCurrent = new Float32Array(n);
      // Border cells stay fully fogged so the map edge is black, not open.
      this.blackTarget.fill(1);
      this.blackCurrent.fill(1);
      this.dimTarget.fill(1);
      this.dimCurrent.fill(1);
    }

    let changed = false;
    for (let y = 0; y < plane.height; y++) {
      for (let x = 0; x < plane.width; x++) {
        const v = plane.visibility[plane.width * plane.height * z + y * plane.width + x]
          ?? FOG_UNSEEN;
        const t = (y + 1) * tw + (x + 1);
        const black = v === FOG_UNSEEN ? 1 : 0;
        const dim = v === FOG_VISIBLE ? 0 : 1;
        if (this.blackTarget[t] !== black || this.dimTarget[t] !== dim) changed = true;
        this.blackTarget[t] = black;
        this.dimTarget[t] = dim;
      }
    }
    if (instant || resized) {
      this.blackCurrent.set(this.blackTarget);
      this.dimCurrent.set(this.dimTarget);
      this.upload();
      this.animating = false;
      return;
    }
    if (changed) this.animating = true;
  }

  /** Step the reveal. Returns true while it is still moving. */
  advance(dtMs: number): boolean {
    if (!this.animating) return false;
    // Frame-rate independent exponential approach, clamped so a long frame
    // cannot overshoot into a visible snap.
    const k = Math.min(1, dtMs / REVEAL_MS);
    let moving = false;
    for (const [cur, tgt] of [
      [this.blackCurrent, this.blackTarget], [this.dimCurrent, this.dimTarget],
    ] as const) {
      for (let i = 0; i < cur.length; i++) {
        const c = cur[i]!;
        const t = tgt[i]!;
        const d = t - c;
        if (Math.abs(d) < 0.004) { cur[i] = t; continue; }
        cur[i] = c + d * k * 2.2;
        moving = true;
      }
    }
    this.upload();
    this.animating = moving;
    return moving;
  }

  /** Jump straight to the target; used by the screenshot harness. */
  settle(): void {
    if (this.blackCurrent.length === 0) return;
    this.blackCurrent.set(this.blackTarget);
    this.dimCurrent.set(this.dimTarget);
    this.upload();
    this.animating = false;
  }

  setVisible(v: boolean): void { this.container.visible = v; }

  /**
   * The cells the camera can see, in MAP coordinates. Stars are built for this
   * rect only: a fully unexplored 144x144 map is 20736 cells and the view is a
   * few hundred, so building the whole map would be two orders of magnitude of
   * sprites for no pixels.
   */
  setView(rect: CellRect): void {
    const v = this.view;
    if (v !== null && v.x0 === rect.x0 && v.y0 === rect.y0
      && v.x1 === rect.x1 && v.y1 === rect.y1) return;
    this.view = rect;
    this.rebuildStars();
  }

  private upload(): void {
    const veil = this.veil;
    if (veil === null) return;
    computeVeilAt(
      this.blackCurrent, this.dimCurrent, this.tw, this.th, veil.buffer, this.sub, true,
    );
    veil.source.update();
    this.rebuildStars();
  }

  /**
   * One pooled sprite per fully-black cell in view. Returns how many, which is
   * what the test asserts against a hand-counted field.
   */
  rebuildStars(): number {
    const rect = this.view;
    if (rect === null || this.tw === 0) { this.hideStars(0); this.hideDust(0); return 0; }
    const frames = this.starFramesOrNull();
    if (frames === null) return 0;

    // Map cell (x, y) is field texel (x+1, y+1); one cell of margin picks up
    // the border ring, which is the shroud past the edge of the map.
    const tx0 = Math.max(0, rect.x0);
    const ty0 = Math.max(0, rect.y0);
    const tx1 = Math.min(this.tw - 1, rect.x1 + 1);
    const ty1 = Math.min(this.th - 1, rect.y1 + 1);

    const dust = this.dustFramesOrNull();
    let used = 0;
    let dusted = 0;
    for (let ty = ty0; ty <= ty1; ty++) {
      for (let tx = tx0; tx <= tx1; tx++) {
        if (dust !== null && cellSettledBlack(this.blackCurrent, this.tw, this.th, tx, ty)) {
          this.place(this.dustPool, this.dustC, dusted, dust[hashCell(tx, ty) % dust.length]!,
            tx, ty, true);
          dusted++;
        }
        if (!cellFullyBlack(this.blackCurrent, this.tw, this.th, tx, ty)) continue;
        this.place(this.starPool, this.starsC, used, frames[hashCell(tx, ty) % frames.length]!,
          tx, ty, false);
        used++;
      }
    }
    this.hideStars(used);
    this.hideDust(dusted);
    return used;
  }

  /**
   * Position one pooled sprite of a layer, growing the pool if this frame needs
   * a sprite the last one did not.
   */
  private place(
    pool: Sprite[], parent: Container, i: number, frame: Texture,
    tx: number, ty: number, additive: boolean,
  ): void {
    let s = pool[i];
    if (s === undefined) {
      s = new Sprite(frame);
      if (additive) s.blendMode = "add";
      pool.push(s);
      parent.addChild(s);
    } else if (s.texture !== frame) {
      s.texture = frame;
    }
    s.visible = true;
    // Texel column tx covers map cell tx-1, whose world origin is
    // (tx-1)*TILE_SIZE - the same offset the veil sprite carries.
    s.position.set((tx - 1) * TILE_SIZE, (ty - 1) * TILE_SIZE);
  }

  private hideStars(used: number): void {
    for (let i = used; i < this.starsUsed; i++) this.starPool[i]!.visible = false;
    this.starsUsed = used;
  }

  private hideDust(used: number): void {
    for (let i = used; i < this.dustUsed; i++) this.dustPool[i]!.visible = false;
    this.dustUsed = used;
  }

  /** `null` in a headless test, where there is no canvas to bake the sky into. */
  /**
   * Sampling for the sky tiles - the stars and the dust, which are 1:1 art
   * and minify like any other sprite. `"linear"` while the presenter's frame
   * is minified (see `ZOOM_OUT_STEPS` in `coords.ts`), `"nearest"` otherwise.
   *
   * The veil field is NOT touched. Its texels are several world pixels each
   * (a magnification, never a minification) and its boundary is resolved in
   * the buffer; a linear sample there would put the soft ramp straight back,
   * as the note on `makeField` says.
   */
  setScaleMode(mode: "nearest" | "linear"): void {
    for (const frames of [sharedStarFrames, sharedDustFrames]) {
      const source = frames?.[0]?.source;
      if (source === undefined || source.scaleMode === mode) continue;
      source.scaleMode = mode;
      source.style.update();
    }
  }

  private starFramesOrNull(): Texture[] | null {
    try {
      return starFrames();
    } catch {
      return null;
    }
  }

  private dustFramesOrNull(): Texture[] | null {
    try {
      return dustFrames();
    } catch {
      return null;
    }
  }

  private makeField(label: string): Field {
    const w = this.tw * this.sub;
    const h = this.th * this.sub;
    const buffer = new Uint8Array(w * h * 4);
    const source = new BufferImageSource({
      resource: buffer,
      width: w,
      height: h,
      format: "rgba8unorm",
      alphaMode: "premultiplied-alpha",
      // NEAREST, because the boundary is already resolved in the buffer. A
      // linear sample here would put the soft ramp straight back.
      scaleMode: "nearest",
      autoGenerateMipmaps: false,
      label,
    });
    const texture = new Texture({ source, label });
    const sprite = new Sprite(texture);
    sprite.blendMode = "normal";
    // Cell texel column (i) is cell (i-1); its subtexels cover that cell, which
    // puts the sprite's corner one whole tile before the world origin.
    sprite.position.set(-TILE_SIZE, -TILE_SIZE);
    sprite.width = this.tw * TILE_SIZE;
    sprite.height = this.th * TILE_SIZE;
    return { buffer, source, texture, sprite };
  }

  dispose(): void {
    this.hideStars(0);
    this.hideDust(0);
    const f = this.veil;
    if (f !== null) {
      f.sprite.destroy();
      f.texture.destroy(false);
      f.source.destroy();
    }
    this.veil = null;
  }

  destroy(): void {
    this.dispose();
    this.container.destroy({ children: true, context: true });
  }
}
